import {
  isAttachmentModel,
  isUploadedAttachmentModel,
  UploadedAttachmentModel,
} from '../../../models/api/attachmentModel';
import {tryParsePublicId} from '../../../models/applications/publicIdTypes';
import {fetchAsync} from '../../../services/http/http';
import {ThunkAsyncAction} from '../../appThunk';
import {ViewerPanelId} from '../../panels/panelStateBase';
import {listLoadedCommentsAction} from './applicationCommentsActions';
import {listLoadedMessageAction} from './applicationsMessagesLoadActions';

/*
 * Actions.
 */

export function applicationDownloadAttachmentAsyncAction(
  panelId: ViewerPanelId,
  conversationId: number,
  messageOrCommentId: string,
  attachmentId: string,
): ThunkAsyncAction<File | undefined> {
  return async (dispatch, _getState) => {
    const messageId = tryParsePublicId(messageOrCommentId, 'message');
    const commentId = tryParsePublicId(messageOrCommentId, 'comment');

    // Passed in value was a message.
    if (messageId) {
      // Ensure the original message is available and loaded.
      const loadedMessages = dispatch(listLoadedMessageAction(panelId, conversationId));
      const message = loadedMessages.find((m) => m.id === messageId);
      if (!message) {
        return undefined;
      }

      // Make sure the attachment is part of the message passed in.
      const attachment = message.attachments.find((a) => tryParsePublicId(attachmentId) === a.linkId);
      if (!attachment || !isUploadedAttachmentModel(attachment)) {
        return undefined;
      }

      // Build and return the file.
      const file = await fetchAttachmentAsync(attachment);

      return file;
    }

    // Passed in value was a comment.
    if (commentId) {
      // Ensure the original comment is available and loaded.
      const loadedComments = dispatch(listLoadedCommentsAction(panelId, conversationId));
      const comment = loadedComments.find((c) => c.id === commentId);
      if (!comment) {
        return undefined;
      }

      // Make sure the attachment is part of the comment passed in.
      const attachment = comment.attachments
        .filter(isAttachmentModel)
        .find((a) => tryParsePublicId(attachmentId) === a.linkId);
      if (!attachment || !isUploadedAttachmentModel(attachment)) {
        return undefined;
      }

      // Build and return the file.
      const file = await fetchAttachmentAsync(attachment);
      return file;
    }

    // We failed to find a message or comment.
    return undefined;
  };
}

/*
 * Helpers.
 */

async function fetchAttachmentAsync(attachment: UploadedAttachmentModel) {
  // Fetch the data and build the file.
  const data = await fetchAsync(attachment.url);
  return new File([buildAttachmentBlobData(data, attachment)], attachment.name, {
    type: attachment.contentType,
  });
}

function buildAttachmentBlobData(data: any, attachment: UploadedAttachmentModel) {
  // If we are reading JSON we need to stringify it since the request will automatically
  // convert the JSON from a string to object.
  if (attachment.contentType === 'application/json') {
    return new Blob([JSON.stringify(data)]);
  }
  return new Blob([data]);
}
